import { useContext } from 'react';
import {
  type QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
  type UseQueryResult,
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useFlags } from '@repo/poly-hooks/flags';
import {
  identitiesKycQueries,
  kycQueries,
  type KycResponse,
} from '@repo/api-kit/queries/due-diligence/kyc';
import { navigationQueries } from '@repo/api-kit/queries/navigation';
import { CurrentOrganizationIdContext } from '@repo/shared-kit/organizations';
import { getKycPollingInterval, mapKycLegacyToAcceptance } from '../utils';

/** @internal */
export const kycLegacyQueryKeys = {
  all: ['kyc-legacy'] as const,
  item: (organizationId: string) => [...kycLegacyQueryKeys.all, organizationId] as const,
};

/**
 * Query options for the legacy KYC fallback path.
 *
 * Uses `queryClient.ensureQueryData` internally so that already-cached
 * membership and identities KYC data is reused instead of re-fetched.
 *
 * @param organizationId - The organization ID used to scope the membership fetch
 *   and the query cache key.
 * @param queryClient - The TanStack Query client, used to leverage cached sub-queries.
 */
export function kycLegacyQueries(queryClient: QueryClient, organizationId: string | null) {
  return queryOptions({
    queryKey: kycLegacyQueryKeys.item(organizationId ?? ''),
    queryFn: async (): Promise<KycResponse> => {
      if (!organizationId) throw new Error('organizationId is required');

      const membership = await queryClient.ensureQueryData(
        navigationQueries.getMembershipsMeAggregate(organizationId)
      );

      const identitiesKyc = await queryClient.ensureQueryData(
        identitiesKycQueries.item(membership.identityId)
      );

      return mapKycLegacyToAcceptance({
        kycStatus: identitiesKyc.kycStatus,
        isReKycRequired: identitiesKyc.isReKycRequired,
        isRenewalPoiRequired: identitiesKyc.isRenewalPoiRequired,
        reKycDueDate: identitiesKyc.reKycDueDate,
        kycRequired: Boolean(membership.kycRequired),
        kycSubmitted: Boolean(membership.kycSubmitted),
        personalInfoProvided: Boolean(membership.personalInfoProvided),
        providerName: identitiesKyc.providerName,
      });
    },
  });
}

/**
 * Hook to fetch KYC status information.
 *
 * @param market - Optional market identifier (e.g. the customer's legal country) to identify
 * customers in register flows.
 * Behaviour is controlled by `feature--boolean-single-kyc`:
 *   - **Enabled**: fetches from the new acceptance API (`/kycb-acceptance/v1/persons/me`)
 *   - **Disabled**: falls back to the legacy endpoints via {@link kycLegacyOptions}
 *
 * @example
 * ```tsx
 * const { data: kycInfo, isLoading, error } = useKycInfo('FR');
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <ErrorMessage error={error} />;
 *
 * return <KycStatus status={kycInfo.kycCurrentStatus} />;
 * ```
 */
export function useKycInfo(market?: string): UseQueryResult<KycResponse> {
  const { featureBooleanSingleKyc } = useFlags();
  const organizationId = useContext(CurrentOrganizationIdContext);
  const queryClient = useQueryClient();

  const newApiResult = useQuery({
    ...kycQueries.item({ market }),
    enabled: Boolean(featureBooleanSingleKyc),
    refetchInterval: ({ state: { data, error } }) => getKycPollingInterval(data, error),
  });

  const fallbackResult = useQuery({
    ...kycLegacyQueries(queryClient, organizationId),
    enabled: Boolean(!featureBooleanSingleKyc && !market && organizationId !== null),
  });

  if (featureBooleanSingleKyc) return newApiResult;

  return fallbackResult;
}

/**
 * Suspense variant of {@link useKycInfo}.
 *
 * Must be rendered inside a `<Suspense>` boundary.
 *
 * @param market - Optional market identifier (e.g. the customer's legal country) to identify
 * customers in register flows.
 * Behaviour is controlled by `feature--boolean-single-kyc`:
 *   - **Enabled**: fetches from the new acceptance API (`/kycb-acceptance/v1/persons/me`)
 *   - **Disabled**: falls back to the legacy endpoints via {@link kycLegacyQueries}
 */
export function useSuspenseKycInfo(market?: string): UseSuspenseQueryResult<KycResponse> {
  const { featureBooleanSingleKyc } = useFlags();
  const organizationId = useContext(CurrentOrganizationIdContext);
  const queryClient = useQueryClient();

  if (featureBooleanSingleKyc) {
    return useSuspenseQuery(kycQueries.item({ market }));
  }

  return useSuspenseQuery(kycLegacyQueries(queryClient, organizationId));
}
